import React, { useEffect, useState } from "react";
import "../styles/AdminCourses.css";
import axios from "axios";
import Sidebar from "../components/AdminSidebar";
import Stats from "../components/Stats";
import CreateModuleModal from "../components/CreateModule";
import CreateSubjectModal from "../components/CreateSubject";
import CreateGroupModal from "../components/CreateGroup";
import Select from "react-select";
import {
  BookCheck,
  BookOpen,
  Calendar,
  Clock,
  GraduationCap,
  Info,
  Lightbulb,
  Plus,
  User,
  Users,
} from "lucide-react";
import { encodePathSegment } from "../utils/apiPath";

export default function AdminCourses() {
  // State to hold course statistics
  const [courseStats, setCourseStats] = useState(null);
  // State to hold loading statistic
  const [courseStatsLoading, setCourseStatsLoading] = useState(true);
  // State to manage active tab
  const [activeTab, setActiveTab] = useState("details");
  // State to show create course modal
  const [showCreateCourseModal, setShowCreateCourseModal] = useState(false);
  // State to hold courses
  const [courses, setCourses] = useState([]);
  // State to hold loading courses
  const [coursesLoading, setCoursesLoading] = useState(true);
  // State to show create group modal
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  // State to hold groups
  const [groups, setGroups] = useState([]);
  // State to hold loading groups
  const [groupsLoading, setGroupsLoading] = useState(true);
  // State to subject loading
  const [subjectsLoading, setSubjectsLoading] = useState(true);
  // State to hold subjects
  const [subjects, setSubjects] = useState([]);
  // State to show create subject modal
  const [showCreateSubjectModal, setShowCreateSubjectModal] = useState(false);
  // State to hold editing subject
  const [editingSubject, setEditingSubject] = useState(null);
  const [formData, setFormData] = useState({});
  // State to hold editing course
  const [editingCourse, setEditingCourse] = useState(null);
  const [courseFormData, setCourseFormData] = useState({});
  // State to hold selected course and group for details view
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  // State to hold course groups, modules, and students for details view
  const [courseGroups, setCourseGroups] = useState([]);
  const [groupModules, setGroupModules] = useState([]);
  const [groupStudents, setGroupStudents] = useState([]);

  // NOWE: wyszukiwarka przedmiotów
  const [showSubjectSearch, setShowSubjectSearch] = useState(false);
  const [subjectSearchTerm, setSubjectSearchTerm] = useState("");

  const handleEditSubject = async (subject) => {
    setEditingSubject(subject);
    
    // Pobierz nauczyciele i grupy przypisane do modułu
    try {
      const [lecturersRes, groupsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/courses/modules/${subject.module_id}/lecturers`),
        fetch(`${process.env.REACT_APP_API_URL}/courses/modules/${subject.module_id}/groups`)
      ]);

      const lecturers = await lecturersRes.json();
      const groups = await groupsRes.json();

      setFormData({
        module_id: subject.module_id,
        module_name: subject.module_name,
        module_description: subject.module_description,
        number_of_hours: subject.number_of_hours,
        lecturer_ids: lecturers.map(l => l.lecturer_id),
        group_ids: groups.map(g => g.group_code),
      });
    } catch (error) {
      console.error("Błąd pobierania danych modułu:", error);
      // W razie błędu ustaw podstawowe dane
      setFormData({
        module_id: subject.module_id,
        module_name: subject.module_name,
        module_description: subject.module_description,
        number_of_hours: subject.number_of_hours,
        lecturer_ids: [],
        group_ids: [],
      });
    }
  };

  // Subject edit modal close
  const handleCloseEdit = () => {
    setEditingSubject(null);
    setFormData({});
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // State to hold selects
  const [selects, setSelects] = useState({
    courses: [],
    groups: [],
    lecturers: [],
    modules: [],
  });

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}/courses/formData`)
      .then((res) => setSelects(res.data));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Upewnij się, że wszystkie wymagane pola są wypełnione
      const payload = {
        module_name: formData.module_name || editingSubject.module_name,
        module_description: formData.module_description !== undefined 
          ? formData.module_description 
          : editingSubject.module_description || "",
        number_of_hours: formData.number_of_hours !== undefined 
          ? formData.number_of_hours 
          : editingSubject.number_of_hours,
        lecturer_ids: formData.lecturer_ids || [],
        group_ids: formData.group_ids || [],
      };

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/courses/modules/${editingSubject.module_id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) throw new Error("Błąd podczas zapisu");

      handleCloseEdit();

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/courses/subjects`
      );
      const data = await response.json();
      setSubjects(data);
    } catch (err) {
      console.error(err);
      alert("Nie udało się zapisać zmian.");
    }
  };

  // Course edit
  const handleEditCourse = (course) => {
    setEditingCourse(course);
    setCourseFormData({
      course_id: course.course_id,
      course_name: course.nazwa_kursu,
      academic_year: course.rok_akademicki,
      description: course.opis_kursu || "",
    });
  };

  const handleCloseEditCourse = () => {
    setEditingCourse(null);
    setCourseFormData({});
  };

  const handleCourseChange = (e) => {
    setCourseFormData({ ...courseFormData, [e.target.name]: e.target.value });
  };

  const handleCourseSubmit = async (e) => {
    e.preventDefault();

    // Utworzenie obiektu do wysłania do API zgodnie z wymaganiami backendu
    const payload = {
      course_name: courseFormData.course_name || editingCourse.nazwa_kursu,
      academic_year: courseFormData.academic_year || editingCourse.rok_akademicki,
      description: courseFormData.description || editingCourse.opis_kursu || "",
    };

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/courses/edit/${editingCourse.course_id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) {
        const errorText = await res.text();
        console.error("Błąd odpowiedzi:", errorText);
        throw new Error("Błąd podczas zapisu kursu");
      }

      handleCloseEditCourse();

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/courses/getCourses`
      );
      const data = await response.json();
      setCourses(data);
    } catch (err) {
      console.error("Błąd podczas zapisu kursu:", err);
      alert("Nie udało się zapisać zmian: " + (err.message || "Nieznany błąd"));
    }
  };

  useEffect(() => {
    // Fetch course statistics
    async function fetchCourseStats() {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/courses/statistics`
        );
        const data = await response.json();
        setCourseStats(data);
      } catch (error) {
        console.error("Error fetching course statistics:", error);
      } finally {
        setCourseStatsLoading(false);
      }
    }
    // Fetch courses
    async function fetchCourses() {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/courses/getCourses`
        );
        const data = await response.json();
        setCourses(data);
      } catch (error) {
        console.error("Error fetching courses:", error);
      } finally {
        setCoursesLoading(false);
      }
    }
    // Fetch groups
    async function fetchGroups() {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/courses/getGroups`
        );
        const data = await response.json();
        setGroups(data);
      } catch (error) {
        console.error("Error fetching groups:", error);
      } finally {
        setGroupsLoading(false);
      }
    }

    async function fetchSubjects() {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/courses/subjects`
        );
        const data = await response.json();
        setSubjects(data);
      } catch (error) {
        console.error("Error fetching subjects:", error);
      } finally {
        setSubjectsLoading(false);
      }
    }

    // Call function to fetch data
    fetchCourseStats();
    fetchCourses();
    fetchSubjects();
    fetchGroups();
  }, []);

  const handleDeleteCourse = async (courseId) => {
    if (!courseId) {
      console.error("Brak ID kursu do usunięcia");
      alert("Nie można usunąć — brak ID kursu!");
      return;
    }

    if (!window.confirm("Czy na pewno chcesz usunąć ten kurs?")) return;

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/courses/delete/${courseId}`,
        { method: "DELETE" }
      );

      if (!res.ok) throw new Error("Nie udało się usunąć kursu");

      setCourses((prev) => prev.filter((c) => c.course_id !== courseId));
      alert("Kurs został usunięty");
      handleCloseEditCourse();
    } catch (err) {
      console.error("Błąd:", err);
      alert("Wystąpił problem przy usuwaniu kursu.");
    }
  };

  const handleDeleteModule = async (moduleId) => {
    if (!window.confirm("Czy na pewno chcesz usunąć ten przedmiot?")) return;

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/courses/delete/module/${moduleId}`,
        { method: "DELETE" }
      );

      if (!res.ok) throw new Error("Nie udało się usunąć przedmiotu");

      const data = await res.json();
      console.log("Usunięto:", data);

      setSubjects((prev) => prev.filter((m) => m.module_id !== moduleId));
      alert("Przedmiot został pomyślnie usunięty");
      handleCloseEdit();
    } catch (err) {
      console.error("Błąd:", err);
      alert("Wystąpił problem przy usuwaniu przedmiotu.");
    }
  };

  // Edit group function
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedGroupEdit, setSelectedGroupEdit] = useState(null);

  const saveGroupChanges = async () => {
    try {
      const clean_code = selectedGroupEdit.group_code?.trim().substring(0, 20);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/courses/group/${selectedGroupEdit.fake_id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            group_name: selectedGroupEdit.group_name,
            new_group_code: clean_code,
          }),
        }
      );

      if (!response.ok) throw new Error("Błąd zapisu grupy");

      const data = await response.json();
      console.log("Zapisano:", data);

      setIsModalOpen(false);

      const resGroups = await fetch(
        `${process.env.REACT_APP_API_URL}/courses/getGroups`
      );
      const updatedGroups = await resGroups.json();
      setGroups(updatedGroups);
    } catch (error) {
      console.error("Błąd zapisu:", error);
      alert("Nie udało się zapisać zmian w grupie.");
    }
  };

  // Fetch groups for selected course
  const fetchGroupsForCourse = async (courseId) => {
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/courses/${courseId}/groups`
    );
    const data = await res.json();
    setCourseGroups(data);
    setSelectedCourse(courseId);
  };

  // Fetch modules and students for selected group
  const fetchDetailsForGroup = async (groupCode) => {
    const cleanCode = encodePathSegment(groupCode);

    const [modulesRes, studentsRes] = await Promise.all([
      fetch(
        `${process.env.REACT_APP_API_URL}/courses/groups/${cleanCode}/modules`
      ),
      fetch(
        `${process.env.REACT_APP_API_URL}/courses/groups/${cleanCode}/students`
      ),
    ]);

    if (!modulesRes.ok || !studentsRes.ok) {
      const errText = await modulesRes.text();
      console.error("Błąd backendu:", errText);
      throw new Error("Błąd pobierania danych grupy");
    }

    const modules = await modulesRes.json();
    const students = await studentsRes.json();

    setGroupModules(modules);
    setGroupStudents(students);
    setSelectedGroup(groupCode.trim());
  };

  // Const to render group view
  const renderGroupsView = () => {
    if (groupsLoading) {
      return <p>Ładowanie grup...</p>;
    }

    return (
      <div className="groups-grid">
        {groups.map((group) => (
          <div
            key={group.fake_id}
            className="group-card"
            onClick={() => {
              setSelectedGroupEdit(group);
              setIsModalOpen(true);
            }}
            style={{
              borderColor:
                selectedGroupEdit?.group_code === group.group_code
                  ? "#16a34a"
                  : "#e5e7eb",
              backgroundColor:
                selectedGroupEdit?.group_code === group.group_code
                  ? "#f0fdf4"
                  : "#fff",
            }}
          >
            <div className="group-header">
              <div>
                <h4 className="group-title">{group.group_name}</h4>
                <div className="group-code">
                  {group.group_code} | {group.course_name} {group.academic_year}
                </div>
              </div>
              <span
                className={`status-badge ${
                  group.status === "aktywna" ? "active" : "inactive"
                }`}
              >
                {group.status === "aktywna" ? "Aktywny" : "Nieaktywny"}
              </span>
            </div>
            <div className="group-meta">
              <div className="meta-item">
                <Users size={14} />
                <span>
                  {group.next_session_lecturer &&
                    group.next_session_lecturer.trim() !== "" && (
                      <div className="lecturers">
                        {group.next_session_lecturer}
                      </div>
                    )}
                </span>
              </div>
              <div className="meta-item">
                <GraduationCap size={14} />
                <span>
                  {group.number_of_students} liczba kursantów zapisanych na
                  grupę
                </span>
              </div>
              <div className="meta-item">
                <BookOpen size={14} />
                <span>{group.number_of_modules} przedmiotów</span>
              </div>
              <div className="meta-item">
                <Lightbulb size={14} />
                <span>{group.next_session_title}</span>
              </div>
              <div className="meta-item">
                <Calendar size={14} />
                <span>{group.next_session_date}</span>
              </div>
              <div className="meta-item">
                <Clock size={14} />
                <span>
                  {group.next_session_start} - {group.next_session_end}
                </span>
              </div>
            </div>
          </div>
        ))}
        {isModalOpen && selectedGroupEdit && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h2 className="">
                Edytuj grupę: {selectedGroupEdit.group_name}
              </h2>
              <div>
                <label>
                  Nazwa grupy
                  <input
                    type="text"
                    className="form-input"
                    value={selectedGroupEdit.group_name}
                    onChange={(e) =>
                      setSelectedGroupEdit({
                        ...selectedGroupEdit,
                        group_name: e.target.value,
                      })
                    }
                  />
                </label>
              </div>

              <div>
                <label>
                  Kod grupy
                  <input
                    type="text"
                    className="form-input"
                    value={selectedGroupEdit.group_code}
                    onChange={(e) =>
                      setSelectedGroupEdit({
                        ...selectedGroupEdit,
                        group_code: e.target.value,
                      })
                    }
                  />
                </label>
              </div>

              <div className="modal-actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => setIsModalOpen(false)}
                >
                  Anuluj
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    saveGroupChanges();
                    setIsModalOpen(false);
                  }}
                >
                  Zapisz
                </button>
                <button
                  className="btn btn-delete"
                  onClick={() => {
                    fetch(
                      `${process.env.REACT_APP_API_URL}/courses/group/${selectedGroupEdit.fake_id}`,
                      {
                        method: "DELETE",
                      }
                    )
                      .then((res) => res.json())
                      .then((data) => {
                        if (data.success) {
                          setGroups(
                            groups.filter(
                              (g) =>
                                g.group_code !== selectedGroupEdit.group_code
                            )
                          );
                          setIsModalOpen(false);
                        } else {
                          alert("Nie udało się usunąć grupy: " + data.error);
                        }
                      })
                      .catch((err) => {
                        console.error(err);
                        alert("Błąd serwera przy usuwaniu grupy");
                      });
                  }}
                >
                  Usuń
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Const to render module view (KURSY)
  const renderCoursesView = () => {
    if (coursesLoading) {
      return <p>Ładowanie kursów...</p>;
    }

    return (
      <div className="courses-grid">
        {courses.map((course) => (
          <div
            key={course.course_id}
            className="course-card"
            onClick={() => handleEditCourse(course)}
          >
            <div className="course-header">
              <div>
                <h4 className="course-title">
                  {course.nazwa_kursu} {course.rok_akademicki}
                </h4>
              </div>
              <span
                className={`course-status ${
                  course.status === "aktywny"
                    ? "status-active"
                    : "status-inactive"
                }`}
              >
                {course.status === "aktywny" ? "Aktywny" : "Nieaktywny"}
              </span>
            </div>
            <div className="course-description">
              {course.opis_kursu || "Brak opisu"}
            </div>
            <div className="course-details-grid">
              <div className="course-detail">
                <Clock size={14} />
                <span>{course.liczba_godzin} godz.</span>
              </div>
              <div className="course-detail">
                <GraduationCap size={14} />
                <span>
                  {course.liczba_studentow} liczba kursantów zapisanych na kurs
                </span>
              </div>
              <div className="course-detail">
                <Users size={14} />
                <span>{course.liczba_grup} grupy</span>
              </div>
              <div className="course-detail">
                <BookOpen size={14} />
                <span>{course.liczba_modulow} przedmiotów</span>
              </div>
            </div>
            <div className="course-teacher-section">
              <div className="teacher-label">PROWADZĄCY:</div>
              {course.wykladowcy && course.wykladowcy.trim() !== "" && (
                <div className="lecturers">
                  <span className="lecturer-text">
                    {course.wykladowcy}
                  </span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // PRZEDMIOTY – 2 kolumny + filtrowanie
  const renderSubjectView = () => {
    if (subjectsLoading) {
      return <p>Ładowanie przedmiotów...</p>;
    }

    const filteredSubjects = subjects.filter((subject) => {
      if (!subjectSearchTerm) return true;
      const needle = subjectSearchTerm.toLowerCase();
      return (
        (subject.module_name || "").toLowerCase().includes(needle) ||
        (subject.course_name || "").toLowerCase().includes(needle)
      );
    });

    return (
      <div className="courses-grid subjects-grid">
        {filteredSubjects.map((subject) => (
          <div
            key={subject.module_id}
            className="course-card"
            onClick={() => handleEditSubject(subject)}
          >
            <div className="course-header">
              <div>
                <h4 className="course-title">{subject.module_name}</h4>
              </div>
              <span
                className={`course-status ${
                  subject.status === "aktywny"
                    ? "status-active"
                    : "status-inactive"
                }`}
              >
                {subject.status === "aktywny" ? "Aktywny" : "Nieaktywny"}
              </span>
            </div>
            <div className="course-description">
              <p>
                <strong>
                  {subject.course_name} {subject.academic_year}
                </strong>
              </p>
            </div>
            <div className="course-description">
              <p>{subject.module_description || "Brak opisu"}</p>
            </div>
            <div className="course-details-grid">
              <div className="course-detail">
                <Clock size={14} />
                <span>{subject.number_of_hours} godz.</span>
              </div>
              <div className="course-detail">
                <Users size={14} />
                <span>{subject.groups_count} grupy</span>
              </div>
              <div className="course-detail">
                <Calendar size={14} />
                <span>{subject.sessions_count} sesji</span>
              </div>
              <div className="course-detail">
                <BookCheck size={14} />
                <span>{subject.assignments_count} zadań</span>
              </div>
            </div>
            <div className="course-teacher-section">
              <div className="teacher-label">PROWADZĄCY:</div>
              {subject.lecturers && subject.lecturers.trim() !== "" && (
                <div className="lecturers">
                  <span className="lecturer-text">
                    {subject.lecturers}
                  </span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderDetailsView = () => {
    if (!selectedCourse) {
      return (
        <div className="details-grid">
          {courses.map((course) => (
            <div
              key={course.course_id}
              className="details-card"
              onClick={() => fetchGroupsForCourse(course.course_id)}
            >
              <h4>
                {course.nazwa_kursu} ({course.rok_akademicki})
              </h4>
            </div>
          ))}
        </div>
      );
    }

    if (!selectedGroup) {
      return (
        <div className="details-grid">
          {courseGroups.map((group) => (
            <div
              key={group.group_code}
              className="group-card-details"
              onClick={() => fetchDetailsForGroup(group.group_code)}
            >
              <h4>{group.group_name}</h4>
              <p>{group.group_code}</p>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="group-details">
        <h3>Przedmioty w grupie {selectedGroup}</h3>
        <ul>
          {groupModules.map((m) => (
            <li key={m.module_id}>
              {m.module_name} ({m.number_of_hours}h)
            </li>
          ))}
        </ul>

        <h3>Studenci</h3>
        <ul>
          {groupStudents.map((s) => (
            <li key={s.student_id}>
              {s.first_name} {s.last_name} ({s.email})
            </li>
          ))}
        </ul>

        <button onClick={() => setSelectedGroup(null)}>Wróć do grup</button>
        <button onClick={() => setSelectedCourse(null)}>
          Wróć do kursów
        </button>
      </div>
    );
  };

  return (
    <div className="admin-dashboard">
      {/* Sidebar */}
      <Sidebar />
      {/* Main Content */}
      <div className="main-content">
        <div className="header">
          <h1 className="header-title">Grupy i Kursy</h1>
        </div>
        <div className="dashboard">
          {/* Kontener na wspólną szerokość */}
          <div className="courses-page-inner">
            <div className="courses-dashboard">
              <h1 className="courses-title">Panel Grup i Kursów</h1>
              <p className="courses-subtitle">
                Kompleksowe zarządzanie grupami kursantów i kursami edukacyjnymi
              </p>
            </div>

            {/* Zakładki */}
            <div className="courses-tabs">
              <div
                className={`view-tabs ${
                  activeTab === "details" ? "active-tab" : ""
                }`}
                onClick={() => setActiveTab("details")}
              >
                <Info size={16} />
                <span>Przeglądaj grupy</span>
              </div>
              <div
                className={`view-tabs ${
                  activeTab === "groups" ? "active-tab" : ""
                }`}
                onClick={() => setActiveTab("groups")}
              >
                <User size={16} />
                <span>Grupy</span>
              </div>
              <div
                className={`view-tabs ${
                  activeTab === "courses" ? "active-tab" : ""
                }`}
                onClick={() => setActiveTab("courses")}
              >
                <BookOpen size={16} />
                <span>Kursy</span>
              </div>
              <div
                className={`view-tabs ${
                  activeTab === "subjects" ? "active-tab" : ""
                }`}
                onClick={() => setActiveTab("subjects")}
              >
                <Lightbulb size={16} />
                <span>Przedmioty</span>
              </div>
            </div>

            {/* Statystyki */}
            <div className="stats-grid">
              {courseStatsLoading ? (
                <p>Ładowanie statystyk...</p>
              ) : (
                <>
                  <Stats
                    label="Łączna liczba grup"
                    value={courseStats.totalGroups}
                  />
                  <Stats
                    label="Aktywne grupy"
                    value={courseStats.activeGroups}
                  />
                  <Stats
                    label="Łączna liczba kursantów"
                    value={courseStats.totalStudents}
                  />
                </>
              )}
            </div>

            {/* Toolbar – przycisk + (dla przedmiotów drugi przycisk) */}
            <div className="toolbar">
              <div className="toolbar-left" />
              <div className="toolbar-right">
                {activeTab === "subjects" && (
                  <button
                    className="btn-toolbar btn-toolbar-secondary"
                    type="button"
                    onClick={() =>
                      setShowSubjectSearch((prevVisible) => !prevVisible)
                    }
                  >
                    Wyszukaj przedmiot
                  </button>
                )}
                <button
                  className="btn-toolbar btn-toolbar-primary"
                  onClick={() => {
                    if (activeTab === "groups") {
                      setShowCreateGroupModal(true);
                    } else if (activeTab === "courses") {
                      setShowCreateCourseModal(true);
                    } else if (activeTab === "subjects") {
                      setShowCreateSubjectModal(true);
                    } else if (activeTab === "details") {
                      return;
                    }
                  }}
                >
                  <Plus size={16} />
                  {activeTab === "groups" && "Dodaj Grupę"}
                  {activeTab === "courses" && "Dodaj Kurs"}
                  {activeTab === "subjects" && "Dodaj Przedmiot"}
                </button>
              </div>
            </div>

            {/* Pasek wyszukiwania przedmiotu – tylko gdy subjects + kliknięty przycisk */}
            {activeTab === "subjects" && showSubjectSearch && (
              <div className="subjects-search-row">
                <input
                  type="text"
                  className="subjects-search-input"
                  placeholder="Wyszukaj przedmiot po nazwie lub kursie..."
                  value={subjectSearchTerm}
                  onChange={(e) => setSubjectSearchTerm(e.target.value)}
                />
              </div>
            )}

            {/* Modale tworzenia */}
            {showCreateGroupModal && (
              <CreateGroupModal
                onClose={() => setShowCreateGroupModal(false)}
              />
            )}
            {showCreateCourseModal && (
              <CreateModuleModal
                onClose={() => setShowCreateCourseModal(false)}
              />
            )}
            {showCreateSubjectModal && (
              <CreateSubjectModal
                onClose={() => setShowCreateSubjectModal(false)}
              />
            )}

            {/* Treść zakładek */}
            {activeTab === "groups" && renderGroupsView()}
            {activeTab === "courses" && renderCoursesView()}
            {activeTab === "subjects" && renderSubjectView()}
            {activeTab === "details" && renderDetailsView()}

            {/* Modale edycji */}
            {editingSubject && (
              <div className="modal-overlay-courses">
                <div className="modal-courses">
                  <h3 className="modal-title-courses">
                    <strong>
                      Edycja przedmiotu: {editingSubject.module_name}
                    </strong>
                  </h3>
                  <form onSubmit={handleSubmit}>
                    <div className="form-group-courses">
                      <label>Nazwa przedmiotu</label>
                      <input
                        type="text"
                        name="module_name"
                        value={formData.module_name || ""}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="form-group-courses">
                      <label>Opis</label>
                      <textarea
                        name="module_description"
                        value={formData.module_description || ""}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="form-group-courses">
                      <label>Liczba godzin</label>
                      <input
                        type="number"
                        name="number_of_hours"
                        value={formData.number_of_hours || ""}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="form-group-courses">
                      <label>Nauczyciele</label>
                      <Select
                        isMulti
                        name="lecturers"
                        value={selects.lecturers
                          .filter((l) => formData.lecturer_ids?.includes(l.lecturer_id))
                          .map((l) => ({
                            value: l.lecturer_id,
                            label: [
                              l.academic_position
                                ? l.academic_position + "."
                                : null,
                              l.first_name,
                              l.last_name,
                            ]
                              .filter(Boolean)
                              .join(" "),
                          }))}
                        options={selects.lecturers.map((l) => ({
                          value: l.lecturer_id,
                          label: [
                            l.academic_position
                              ? l.academic_position + "."
                              : null,
                            l.first_name,
                            l.last_name,
                          ]
                            .filter(Boolean)
                            .join(" "),
                        }))}
                        onChange={(selected) => {
                          setFormData({
                            ...formData,
                            lecturer_ids: selected
                              ? selected.map((s) => s.value)
                              : [],
                          });
                        }}
                      ></Select>
                    </div>
                    <div className="form-group-courses">
                      <label>Grupy</label>
                      <Select
                        name="groups"
                        isMulti
                        value={selects.groups
                          .filter((g) => formData.group_ids?.includes(g.group_code))
                          .map((g) => ({
                            value: g.group_code,
                            label: [g.group_name].filter(Boolean).join(" - "),
                          }))}
                        options={selects.groups.map((g) => ({
                          value: g.group_code,
                          label: [g.group_name].filter(Boolean).join(" - "),
                        }))}
                        onChange={(selected) => {
                          setFormData({
                            ...formData,
                            group_ids: selected
                              ? selected.map((s) => s.value)
                              : [],
                          });
                        }}
                      ></Select>
                    </div>

                    <div className="form-actions-courses">
                      <button
                        type="button"
                        className="btn-courses btn-secondary-courses"
                        onClick={handleCloseEdit}
                      >
                        Anuluj
                      </button>
                      <button
                        type="submit"
                        className="btn-courses btn-primary-courses"
                      >
                        Zapisz
                      </button>
                      <button
                        type="button"
                        className="btn btn-delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteModule(editingSubject.module_id);
                        }}
                      >
                        Usuń
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
            {editingCourse && (
              <div className="modal-overlay-courses">
                <div className="modal-courses">
                  <h3 className="modal-title-courses">
                    <strong>
                      Edycja kursu: {editingCourse.course_name}
                    </strong>
                  </h3>
                  <form onSubmit={handleCourseSubmit}>
                    <div className="form-group-courses">
                      <label>Nazwa kursu</label>
                      <input
                        type="text"
                        name="course_name"
                        value={courseFormData.course_name || ""}
                        onChange={handleCourseChange}
                      />
                    </div>
                    <div className="form-group-courses">
                      <label>Rok akademicki</label>
                      <input
                        type="text"
                        name="academic_year"
                        value={courseFormData.academic_year || ""}
                        onChange={handleCourseChange}
                      />
                    </div>
                    <div className="form-group-courses">
                      <label>Opis</label>
                      <textarea
                        name="description"
                        value={courseFormData.description || ""}
                        onChange={handleCourseChange}
                      />
                    </div>
                    <div className="form-actions-courses">
                      <button
                        type="button"
                        className="btn-courses btn-secondary-courses"
                        onClick={handleCloseEditCourse}
                      >
                        Anuluj
                      </button>
                      <button
                        type="submit"
                        className="btn-courses btn-primary-courses"
                      >
                        Zapisz
                      </button>
                      <button
                        type="button"
                        className="btn btn-delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteCourse(editingCourse.course_id);
                        }}
                      >
                        Usuń
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
          {/* /courses-page-inner */}
        </div>
      </div>
    </div>
  );
}
